import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { dashboardAPI, operationAPI, surveyAPI } from '../services/api'
import StatCard from '../components/StatCard'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import 'bootstrap-icons/font/bootstrap-icons.css'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip)

// Images in CRA should live under `public/images` and be referenced via PUBLIC_URL.
const PUBLIC_URL = process.env.PUBLIC_URL || ''

const SLIDES = [
  `${PUBLIC_URL}/images/image1.jpeg`,
  `${PUBLIC_URL}/images/image2.avif`,
  `${PUBLIC_URL}/images/image3.avif`,
  `${PUBLIC_URL}/images/image4.avif`,
  `${PUBLIC_URL}/images/image5.avif`,
  `${PUBLIC_URL}/images/image6.avif`,
]
const SLIDE_MS = 5000

// 'YYYY-MM-DD' in local time, so "today" matches the operations calendar
const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const formatDay = (iso) => {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
}

const relativeDay = (iso, todayIso) => {
  if (iso === todayIso) return 'Today'
  const [y, m, d] = iso.split('-').map(Number)
  const [ty, tm, td] = todayIso.split('-').map(Number)
  const days = Math.round((new Date(y, m - 1, d) - new Date(ty, tm - 1, td)) / 86400000)
  if (days === 1) return 'Tomorrow'
  return `In ${days} days`
}

const SectionTitle = ({ icon, title, action, onAction }) => (
  <div className="mb-4 flex items-center justify-between gap-3">
    <h2 className="flex min-w-0 items-center gap-2.5 whitespace-nowrap text-lg font-semibold">
      <i className={`bi ${icon} icon-premium`}></i>
      <span className="text-premium">{title}</span>
    </h2>
    {action && (
      <button
        type="button"
        onClick={onAction}
        className="group inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-medium text-slate-500 transition-colors hover:text-[#ea580c]"
      >
        {action}
        <i className="bi bi-arrow-right transition-transform duration-200 group-hover:translate-x-0.5"></i>
      </button>
    )}
  </div>
)

const EmptyState = ({ icon, text }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-sm text-slate-400">
    <i className={`bi ${icon} text-2xl`}></i>
    {text}
  </div>
)

const Home = () => {
  const { isAuthenticated, isStaff, isClientuser, isSuperuser, user } = useAuth()
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [operations, setOperations] = useState([])
  const [recentSurveys, setRecentSurveys] = useState([])
  const [loading, setLoading] = useState(true)
  const [slideIndex, setSlideIndex] = useState(0)

  const hasStatsAccess = isAuthenticated && (isStaff || isClientuser)
  const now = new Date()
  const todayIso = isoDay(now)

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false)
      return
    }
    // Each panel loads independently so one failing endpoint doesn't blank the page
    const loaders = [
      operationAPI
        .list()
        .then((res) => setOperations(Array.isArray(res.data) ? res.data : res.data?.results || []))
        .catch((error) => console.error('Error fetching operations:', error)),
    ]
    if (isStaff || isClientuser) {
      const params = new URLSearchParams()
      params.append('skip_date_filter', 'true')
      loaders.push(
        dashboardAPI
          .getStats(params)
          .then((res) => {
            if (res.data.status === 'success') setStats(res.data.data)
          })
          .catch((error) => console.error('Error fetching stats:', error))
      )
    }
    if (isStaff) {
      loaders.push(
        surveyAPI
          .list({ page: 1 })
          .then((res) => setRecentSurveys((res.data?.results || res.data || []).slice(0, 6)))
          .catch((error) => console.error('Error fetching surveys:', error))
      )
    }
    Promise.all(loaders).finally(() => setLoading(false))
  }, [isAuthenticated, isStaff, isClientuser])

  // Restarts on every slide change so a manual pick gets the full interval, matching the progress bar
  useEffect(() => {
    const timer = setTimeout(() => setSlideIndex((i) => (i + 1) % SLIDES.length), SLIDE_MS)
    return () => clearTimeout(timer)
  }, [slideIndex])

  // Key figures — same set, labels and role rules as the Dashboard cards
  const allQuickStats = [
    {
      icon: 'bi-building',
      label: isClientuser ? 'Your Team' : 'Unique Clients',
      value: stats?.surveys_by_client ? new Set(stats.surveys_by_client.map((c) => c.client_name)).size : 0,
      showFor: ['staff', 'clientuser'],
      order: 1,
    },
    {
      icon: 'bi-people',
      label: 'Active Surveyors',
      value: stats?.surveys_by_surveyor?.length || 0,
      showFor: ['staff'],
      order: 2,
    },
    {
      icon: 'bi-clipboard-data',
      label: 'Total Surveys',
      value: (stats?.total_surveys || 0).toLocaleString('en-US'),
      showFor: ['staff', 'clientuser'],
      order: isClientuser ? 2 : 3,
    },
    {
      icon: 'bi-globe',
      label: isClientuser ? 'Your Total Countries' : 'Countries Covered',
      value: stats?.surveys_by_country ? new Set(stats.surveys_by_country.map((c) => c.survey_country)).size : 0,
      showFor: ['staff', 'clientuser'],
      order: isClientuser ? 3 : 4,
    },
    {
      icon: 'bi-fuel-pump',
      label: isClientuser ? 'Your Recovered Fuel (MT)' : 'Recovered Fuel (MT)',
      value: stats?.total_recovered_fuel_mt ? parseFloat(stats.total_recovered_fuel_mt).toFixed(3) : '0.000',
      showFor: ['superuser', 'clientuser'],
      order: isClientuser ? 4 : 5,
    },
    {
      icon: 'bi-cash-stack',
      label: isClientuser ? 'Your Total Saving' : 'Total Saving',
      value: stats?.total_saving
        ? `$${parseFloat(stats.total_saving).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : '$0.00',
      showFor: ['superuser', 'clientuser'],
      order: isClientuser ? 5 : 6,
    },
  ]

  const quickStats = allQuickStats
    .filter((stat) => {
      if (isSuperuser) return stat.showFor.includes('superuser') || stat.showFor.includes('staff')
      if (isClientuser) return stat.showFor.includes('clientuser')
      if (isStaff) return stat.showFor.includes('staff')
      return false
    })
    .sort((a, b) => a.order - b.order)

  const monthly = useMemo(() => {
    const months = Array.isArray(stats?.surveys_by_month) ? stats.surveys_by_month.slice(-12) : []
    if (months.length === 0) return null
    return {
      labels: months.map((m) => m.month || ''),
      counts: months.map((m) => m.count || 0),
    }
  }, [stats?.surveys_by_month])

  const monthlyChart = useMemo(() => {
    if (!monthly) return null
    return {
      labels: monthly.labels.map((label) => label.split(' ')[0]),
      datasets: [
        {
          label: 'Surveys',
          data: monthly.counts,
          backgroundColor: (ctx) => {
            const { chart } = ctx
            const { ctx: c, chartArea } = chart
            if (!chartArea) return '#f97316'
            const g = c.createLinearGradient(0, chartArea.bottom, 0, chartArea.top)
            g.addColorStop(0, '#ea580c')
            g.addColorStop(1, '#fbbf24')
            return g
          },
          hoverBackgroundColor: '#c2410c',
          borderRadius: { topLeft: 4, topRight: 4 },
          borderSkipped: 'bottom',
          maxBarThickness: 28,
        },
      ],
    }
  }, [monthly])

  const monthlyOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1c1410',
          padding: 10,
          displayColors: false,
          callbacks: {
            title: (items) => monthly?.labels[items[0].dataIndex] || '',
            label: (item) => `${item.parsed.y} surveys`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: { color: '#78716c', font: { size: 11 } },
        },
        y: {
          beginAtZero: true,
          border: { display: false },
          grid: { color: 'rgba(120, 113, 108, 0.12)' },
          ticks: { color: '#a8a29e', font: { size: 11 }, maxTicksLimit: 5, precision: 0 },
        },
      },
    }),
    [monthly]
  )

  const monthTotal = monthly ? monthly.counts.reduce((sum, n) => sum + n, 0) : 0
  const lastMonth = monthly ? monthly.counts[monthly.counts.length - 1] : 0
  const prevMonth = monthly && monthly.counts.length > 1 ? monthly.counts[monthly.counts.length - 2] : null
  // A month still in progress isn't compared against a full one
  const lastMonthInProgress =
    !!monthly &&
    monthly.labels[monthly.labels.length - 1] ===
      now.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })

  const topTypes = useMemo(() => {
    const types = Array.isArray(stats?.surveys_by_type) ? stats.surveys_by_type : []
    const total = types.reduce((sum, t) => sum + (t.count || 0), 0)
    const top = types.slice(0, 5)
    const rest = types.slice(5).reduce((sum, t) => sum + (t.count || 0), 0)
    const rows = top.map((t) => ({ label: t.survey_type, count: t.count }))
    if (rest > 0) rows.push({ label: 'Other types', count: rest, muted: true })
    const max = Math.max(...rows.map((r) => r.count), 1)
    return { rows, total, max }
  }, [stats?.surveys_by_type])

  const upcoming = useMemo(
    () =>
      operations
        .filter((op) => op.start_date && (op.end_date || op.start_date) >= todayIso)
        .sort((a, b) => a.start_date.localeCompare(b.start_date))
        .slice(0, 6),
    [operations, todayIso]
  )

  const displayName = user
    ? user.welcome_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username
    : ''

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-kbl-neon-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#faf8f6]">
      <style>{`
        .home-reveal { animation: homeReveal 0.6s cubic-bezier(0.16, 1, 0.3, 1) both; }
        @keyframes homeReveal {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .home-slide {
          position: absolute; inset: 0; width: 100%; height: 100%;
          object-fit: cover; opacity: 0; transition: opacity 1s ease;
        }
        .home-slide.active { opacity: 1; animation: homeKenBurns ${SLIDE_MS + 1000}ms ease-out forwards; }
        @keyframes homeKenBurns { from { transform: scale(1); } to { transform: scale(1.08); } }
        .home-slide-progress { animation: homeSlideProgress linear forwards; }
        @keyframes homeSlideProgress { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        @media (prefers-reduced-motion: reduce) {
          .home-reveal, .home-slide.active, .home-slide-progress { animation: none; }
          .home-slide { transition: none; }
        }
      `}</style>

      {/* Hero */}
      <section className="border-b border-[#efe3d6] bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-8 px-4 pb-8 pt-5 sm:px-6 lg:grid-cols-[1.1fr_1fr] lg:gap-12 lg:px-8 lg:pb-10 lg:pt-6">
          <div className="home-reveal">
            <p className="text-xs font-semibold uppercase tracking-[0.2em]">
              <span className="text-premium">Marine Survey Management</span>
            </p>
            <h1 className="text-premium mt-2 pb-1 text-5xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl xl:text-7xl">
              KBL Marine
            </h1>
            <p className="mt-4 text-xl font-medium text-slate-700 sm:text-2xl">
              {displayName ? (
                <>
                  Welcome back, <span className="font-bold text-slate-900">{displayName}</span>
                </>
              ) : (
                'Welcome aboard'
              )}
            </p>
            <p className="mt-2 max-w-xl text-base leading-relaxed text-slate-600">
              {now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              {upcoming.length > 0 && (
                <>
                  {' · '}
                  <span className="font-semibold text-[#c2410c]">
                    {upcoming.filter((op) => op.start_date <= todayIso).length || 'No'} operation
                    {upcoming.filter((op) => op.start_date <= todayIso).length === 1 ? '' : 's'} today
                  </span>
                </>
              )}
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              {isStaff && (
                <button
                  type="button"
                  onClick={() => navigate('/surveys/new')}
                  className="bg-premium inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(234,88,12,0.35)] transition-shadow hover:shadow-[0_6px_20px_rgba(234,88,12,0.45)]"
                >
                  <i className="bi bi-plus-lg"></i>
                  New Survey
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/operations')}
                className="inline-flex items-center gap-2 rounded-lg border border-[#efe3d6] bg-white px-5 py-2.5 text-sm font-semibold text-[#c2410c] transition-colors hover:border-[#ea580c] hover:bg-[#fff7ed]"
              >
                <i className="bi bi-calendar3"></i>
                Operations Calendar
              </button>
              {(isStaff || isClientuser) && (
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="inline-flex items-center gap-2 rounded-lg border border-[#efe3d6] bg-white px-5 py-2.5 text-sm font-semibold text-[#c2410c] transition-colors hover:border-[#ea580c] hover:bg-[#fff7ed]"
                >
                  <i className="bi bi-graph-up"></i>
                  Dashboard
                </button>
              )}
            </div>
          </div>

          <div className="home-reveal" style={{ animationDelay: '80ms' }}>
            <div className="card-premium overflow-hidden p-1.5">
              <div className="relative aspect-[16/9] overflow-hidden rounded-xl bg-slate-100">
                {SLIDES.map((src, index) => (
                  <img key={src} src={src} alt="" className={`home-slide ${index === slideIndex ? 'active' : ''}`} />
                ))}
                <div className="absolute inset-x-0 bottom-0 flex justify-center gap-2 bg-gradient-to-t from-black/40 to-transparent pb-4 pt-10">
                  {SLIDES.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setSlideIndex(index)}
                      className={`relative h-1.5 overflow-hidden rounded-full transition-all duration-300 ${
                        index === slideIndex ? 'w-10 bg-white/40' : 'w-1.5 bg-white/55 hover:bg-white/80'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    >
                      {index === slideIndex && (
                        <span
                          key={slideIndex}
                          className="home-slide-progress absolute inset-0 origin-left rounded-full bg-white"
                          style={{ animationDuration: `${SLIDE_MS}ms` }}
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl space-y-10 px-4 py-8 sm:px-6 lg:px-8">
        {/* Key figures */}
        {hasStatsAccess && stats && quickStats.length > 0 && (
          <section>
            <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 ${quickStats.length >= 6 ? 'xl:grid-cols-6' : quickStats.length === 5 ? 'xl:grid-cols-5' : 'xl:grid-cols-4'}`}>
              {quickStats.map((stat, index) => (
                <StatCard
                  key={stat.label}
                  icon={stat.icon}
                  label={stat.label}
                  value={stat.value}
                  onClick={() => navigate('/dashboard')}
                  className="home-reveal"
                  style={{ animationDelay: `${120 + index * 60}ms` }}
                />
              ))}
            </div>
          </section>
        )}

        {/* Activity: monthly volume + survey mix */}
        {hasStatsAccess && stats && (monthly || topTypes.rows.length > 0) && (
          <section className="grid grid-cols-1 gap-5 lg:grid-cols-5">
            <div className="card-premium home-reveal p-5 lg:col-span-3" style={{ animationDelay: '200ms' }}>
              <SectionTitle icon="bi-bar-chart-line" title="Surveys per month" />
              {monthly ? (
                <>
                  <div className="mb-4 flex flex-wrap items-end gap-x-8 gap-y-2">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500">Last 12 months</p>
                      <p className="text-premium text-2xl font-bold tabular-nums">{monthTotal.toLocaleString('en-US')}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500">
                        {monthly.labels[monthly.labels.length - 1]}
                        {lastMonthInProgress && ' · so far'}
                      </p>
                      <p className="flex items-baseline gap-2 text-2xl font-bold tabular-nums text-slate-900">
                        {lastMonth}
                        {!lastMonthInProgress && prevMonth !== null && prevMonth > 0 && (
                          <span className={`text-xs font-semibold ${lastMonth >= prevMonth ? 'text-emerald-600' : 'text-slate-500'}`}>
                            <i className={`bi ${lastMonth >= prevMonth ? 'bi-arrow-up-right' : 'bi-arrow-down-right'}`}></i>{' '}
                            {Math.abs(Math.round(((lastMonth - prevMonth) / prevMonth) * 100))}% vs previous
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="h-56">
                    <Bar data={monthlyChart} options={monthlyOptions} aria-label="Surveys per month, last 12 months" role="img" />
                  </div>
                </>
              ) : (
                <EmptyState icon="bi-bar-chart" text="No monthly data yet" />
              )}
            </div>

            <div className="card-premium home-reveal p-5 lg:col-span-2" style={{ animationDelay: '260ms' }}>
              <SectionTitle icon="bi-pie-chart" title="Survey mix" action="Dashboard" onAction={() => navigate('/dashboard')} />
              {topTypes.rows.length > 0 ? (
                <ul className="space-y-3.5">
                  {topTypes.rows.map((row) => (
                    <li key={row.label} title={`${row.label}: ${row.count}`}>
                      <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                        <span className={`truncate ${row.muted ? 'text-slate-500' : 'font-medium text-slate-800'}`}>{row.label}</span>
                        <span className="shrink-0 tabular-nums text-slate-900">
                          <span className="font-semibold">{row.count.toLocaleString('en-US')}</span>
                          <span className="ml-1.5 text-xs text-slate-400">
                            {topTypes.total ? Math.round((row.count / topTypes.total) * 100) : 0}%
                          </span>
                        </span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-[#f5ede6]">
                        <div
                          className={`h-full rounded-full ${row.muted ? 'bg-stone-300' : 'bg-premium'}`}
                          style={{ width: `${Math.max((row.count / topTypes.max) * 100, 2)}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState icon="bi-pie-chart" text="No survey types yet" />
              )}
            </div>
          </section>
        )}

        {/* Upcoming operations + recent surveys */}
        <section className={`grid grid-cols-1 gap-5 ${isStaff ? 'lg:grid-cols-2' : ''}`}>
          <div className="card-premium home-reveal p-5" style={{ animationDelay: '320ms' }}>
            <SectionTitle icon="bi-calendar-event" title="Upcoming operations" action="Calendar" onAction={() => navigate('/operations')} />
            {upcoming.length > 0 ? (
              <ul className="divide-y divide-[#f3ebe3]">
                {upcoming.map((op) => (
                  <li key={op.id}>
                    <button
                      type="button"
                      onClick={() => navigate('/operations')}
                      className="flex w-full items-center gap-4 rounded-lg px-2 py-3 text-left transition-colors hover:bg-[#fff7ed]"
                    >
                      <span className="flex w-14 shrink-0 flex-col items-center rounded-lg border border-[#fed7aa] bg-[#fff7ed] py-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-[#c2410c]">
                          {formatDay(op.start_date).split(' ')[1]}
                        </span>
                        <span className="text-lg font-bold leading-none text-slate-900">{formatDay(op.start_date).split(' ')[0]}</span>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-semibold text-slate-900">{op.title}</span>
                        <span className="block truncate text-sm text-slate-500">
                          {op.survey_type || 'Operation'}
                          {op.survey_ref ? ` · ${op.survey_ref}` : ''}
                        </span>
                      </span>
                      <span
                        className={`hidden shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold sm:inline-block ${
                          op.start_date <= todayIso ? 'bg-premium text-white' : 'bg-[#f5ede6] text-[#9a3412]'
                        }`}
                      >
                        {op.start_date <= todayIso ? 'Today' : relativeDay(op.start_date, todayIso)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState icon="bi-calendar-check" text="No upcoming operations" />
            )}
          </div>

          {isStaff && (
            <div className="card-premium home-reveal p-5" style={{ animationDelay: '380ms' }}>
              <SectionTitle icon="bi-clock-history" title="Recent surveys" action="All surveys" onAction={() => navigate('/surveys')} />
              {recentSurveys.length > 0 ? (
                <ul className="divide-y divide-[#f3ebe3]">
                  {recentSurveys.map((survey) => (
                    <li key={survey.reference_number}>
                      <button
                        type="button"
                        onClick={() => navigate(`/surveys/${survey.reference_number}/edit`)}
                        className="flex w-full items-center gap-4 rounded-lg px-2 py-3 text-left transition-colors hover:bg-[#fff7ed]"
                      >
                        <span className="badge-metallic h-9 w-9 text-sm">
                          <i className="bi bi-file-earmark-text"></i>
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex min-w-0 flex-col sm:flex-row sm:items-center sm:gap-2">
                            <span className="truncate font-semibold text-slate-900">{survey.vessel_name || 'Unnamed vessel'}</span>
                            <span className="shrink-0 text-xs font-semibold tabular-nums text-[#c2410c]">{survey.reference_number}</span>
                          </span>
                          <span className="block truncate text-sm text-slate-500">
                            {[survey.client_name, survey.survey_port].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        <span
                          className={`hidden shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold sm:inline-flex ${
                            survey.cancelled
                              ? 'bg-stone-100 text-stone-500'
                              : survey.invoice_sent === 'Yes'
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-[#fff7ed] text-[#9a3412]'
                          }`}
                        >
                          <i
                            className={`bi ${
                              survey.cancelled ? 'bi-x-circle' : survey.invoice_sent === 'Yes' ? 'bi-check-circle' : 'bi-hourglass-split'
                            }`}
                          ></i>
                          {survey.cancelled ? 'Cancelled' : survey.invoice_sent === 'Yes' ? 'Invoiced' : 'Open'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState icon="bi-clipboard" text="No surveys yet" />
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

export default Home
